import React, { useEffect, useRef, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { CopyButton } from "./ui/copy-button";
import RotatingTextAnimation from "./ui/RotatingTextAnimation";

interface ThinkingAccordionProps {
  value?: string;
  id: string | number;
  defaultOpen?: boolean;
  className?: string;
  title?: string;
  children?: React.ReactNode;
  copyValue?: string;
}

const ThinkingAccordion: React.FC<ThinkingAccordionProps> = ({
  value,
  id,
  defaultOpen = false,
  className = "",
  title = "Thinking",
  children,
  copyValue,
}) => {
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    defaultOpen ? `item-${id}` : undefined,
  );

  useEffect(() => {
    if (defaultOpen) {
      setAccordionValue(`item-${id}`);
    }
  }, [defaultOpen, id]);

  // Auto-open when content is actively growing (streaming in).
  // Skips the initial mount to avoid opening old blocks on page load.
  const isFirstRenderRef = useRef(true);
  const prevValueLengthRef = useRef(value?.length ?? 0);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevValueLengthRef.current = value?.length ?? 0;
      return;
    }
    const currentLength = value?.length ?? 0;
    if (defaultOpen && currentLength > prevValueLengthRef.current) {
      setAccordionValue(`item-${id}`);
    }
    prevValueLengthRef.current = currentLength;
  }, [value, id, defaultOpen]);

  if (!value && !children) {
    return null;
  }

  return (
    <div className={`mb-3 w-full rounded-lg border border-gray-200 bg-white ${className}`}>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value={`item-${id}`} className="border-0">
          <AccordionTrigger className="group overflow-hidden rounded-t-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50/80 hover:no-underline data-[state=open]:rounded-none data-[state=open]:border-b data-[state=open]:border-gray-200">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div
                  className={`h-1.5 w-1.5 rounded-full ${
                    defaultOpen ? "animate-pulse bg-green-500" : "bg-gray-400"
                  }`}
                />
                <div
                  className={`absolute inset-0 h-1.5 w-1.5 rounded-full ${
                    defaultOpen ? "animate-ping bg-green-400" : ""
                  }`}
                />
              </div>
              <RotatingTextAnimation
                key={title}
                phrases={[title]}
                disableBlur
                className="text-sm font-medium"
                interval={3000}
              />
            </div>
          </AccordionTrigger>
          <AccordionContent className="group p-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="relative rounded-b-lg bg-gray-50/30 px-4 py-4">
              <div className="w-full whitespace-pre-wrap font-mono text-sm leading-relaxed text-gray-700">
                {children ? children : value}
              </div>
              <div className="absolute right-4 top-4 z-10">
                <CopyButton
                  text={copyValue ?? value ?? (typeof children === "string" ? children : "")}
                  className="bg-white/70 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                  title="Copy"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default ThinkingAccordion;
